import {
  type CareerHistoryFormValues,
  normaliseCareerHistory,
} from '@seek/ca-myprofile-utils';
import { CareerHistoryForm } from '@seek/profile-edit-ui';
import { useState } from 'react';

import { onSubmitFail } from 'src/components/Card/onSubmitFail';
import type { DirtyFormProps } from 'src/components/FormDrawer/FormDrawer';
import { useEnterReadMode } from 'src/components/Router/useEnterReadMode';
import { useId } from 'src/components/Router/useId';
import { usePage } from 'src/components/Router/usePage';
import { EDIT, ADD } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import { useFocus } from 'src/modules/hooks/useFocus';
import type { ActionName } from 'src/types';
import * as Schema from 'src/types/graphql';

import { useSuggestedSkills } from '../../Skills/data';
import {
  useAddUnconfirmedRole,
  useGetConfirmedRoles,
  useGetUnconfirmedRoles,
  useRejectUnconfirmedRole,
  useRemoveConfirmedRole,
  useUpdateConfirmedRoles,
} from '../data';

const WAIT_FOR_SUGGESTED_SKILLS_TO_REFRESH = 5000;

export const EditCareerHistory = ({ onFormDirtyChange }: DirtyFormProps) => {
  const enterReadMode = useEnterReadMode();
  const page = usePage();
  const { zone } = useAppConfig();
  const { confirmedRoles, loading: confirmedRolesLoading } =
    useGetConfirmedRoles();
  const { unconfirmedRoles, loading: unconfirmedRolesLoading } =
    useGetUnconfirmedRoles();
  const { refetch } = useSuggestedSkills();

  const id = useId() || '';

  const activeRole = [...confirmedRoles, ...unconfirmedRoles].find(
    (role) => role.id === id,
  );
  const isUnconfirmed = Boolean(
    unconfirmedRoles.find((role) => role.id === id),
  );

  const [mode] = useState<ActionName>(activeRole ? EDIT : ADD);

  const initialValues = activeRole;
  const validateOnMount = isUnconfirmed;
  const canDelete = confirmedRoles.length > 1 || isUnconfirmed;

  const [updateConfirmedRoles] = useUpdateConfirmedRoles(mode);
  const [removeConfirmedRole] = useRemoveConfirmedRole();
  const [addUnconfirmedRole] = useAddUnconfirmedRole(EDIT);
  const [rejectUnconfirmedRole] = useRejectUnconfirmedRole();

  const refetchSuggestedSkills = () => {
    setTimeout(() => {
      refetch();
    }, WAIT_FOR_SUGGESTED_SKILLS_TO_REFRESH);
  };

  const submitForm = (params: {
    dirty: boolean;
    values: CareerHistoryFormValues;
  }) => {
    const { values, dirty } = params;
    if (isUnconfirmed) {
      addUnconfirmedRole({
        role: normaliseCareerHistory(values) as Schema.ConfirmRoleInput, // TODO: casting to avoid sending deprecated `function` and `industry` fields. Can remove once removed from graph schema.
      });
    } else if (dirty) {
      updateConfirmedRoles({
        role: normaliseCareerHistory(values) as Schema.ConfirmRoleInput, // TODO: casting to avoid sending deprecated `function` and `industry` fields. Can remove once removed from graph schema.
      });
    }
    refetchSuggestedSkills();
    enterReadMode();
  };

  const deleteCareerRole = () => {
    if (isUnconfirmed) {
      const selectedRole = unconfirmedRoles.find((role) => role.id === id);
      rejectUnconfirmedRole({
        role: {
          id,
          dataType: Schema.UnconfirmedDataType.CareerHistory,
          tracking: selectedRole?.tracking || null,
        },
      });
    } else {
      removeConfirmedRole({ input: { id } });
      refetchSuggestedSkills();
    }
    enterReadMode();
  };

  const onSubmitFailFn = (errors: string[]) => {
    onSubmitFail(errors, page);
  };

  const achievementsRef = useFocus<HTMLTextAreaElement>({
    card: 'career-history',
    fieldName: 'achievements',
  });

  const isEdit = mode === EDIT;

  if (confirmedRolesLoading || unconfirmedRolesLoading) {
    return null;
  }

  return (
    <CareerHistoryForm
      zone={zone}
      initialValues={initialValues}
      onSubmit={submitForm}
      onCancel={enterReadMode}
      onSubmitFail={onSubmitFailFn}
      achievementsRef={achievementsRef}
      onFormDirtyChange={onFormDirtyChange}
      validateOnMount={validateOnMount}
      scrollToTopOnMobile={false}
      {...(isEdit && canDelete && { onDelete: deleteCareerRole })}
    />
  );
};
